/**
 * `uplinkStats.ts` — how much of the learner's microphone audio left the
 * device, and how much of it reached the server, for one recording window.
 *
 * A recording window is one spoken turn as the microphone saw it: push-to-talk
 * opens it with a TAP (Spacebar or the input field) and it closes when server
 * VAD ends the turn, the no-speech timeout fires or the session mutes; constant
 * listening opens one window for as long as the mic is live. Nothing is held.
 *
 * A trace could show a turn whose transcript was cut short ("Spark." from a
 * 4.6 s push-to-talk window, 2026-10-14) only indirectly: by comparing the
 * server's audio clock (`audio_start_ms` / `audio_end_ms`) with wall time, the
 * turn was ~2.2 s short. That proves audio went missing; it cannot say WHERE.
 * One note per recording window (`ptt.uplink` / `microphone.uplink`) names it:
 *
 *   - `packets_sent` low for the window: the DEVICE did not send (mic, a
 *     stalled tab). One Opus packet is 20 ms, so a healthy 4.6 s window is ~230.
 *   - `lost` / `loss` high: the packets left and the NETWORK dropped them.
 *   - neither: the audio reached the server, so look at transcription.
 *
 * Counters, codes and timings only — never audio, never text.
 *
 * ⚠ The loss figures come from the server's RTCP receiver reports, which
 * OpenAI sends about once a second (measured 2026-10-14: `remote-inbound-rtp`
 * advanced one `roundTripTimeMeasurements` per second). When the window closes
 * the latest report still lags it by up to a second, so the transport reads a SECOND
 * sample {@link K_UPLINK_REPORT_SETTLE_MS} later and computes loss over that
 * whole window, its denominator the packets sent over the same window. A
 * disabled track keeps sending silence, so the window's tail is real traffic.
 */

/** How long after the window closes the loss sample waits for a fresh receiver report. */
export const K_UPLINK_REPORT_SETTLE_MS = 1500;

/** One `getStats()` reading of the audio sender, cumulative since the call began. */
export interface UplinkSnapshot {
  readonly packetsSent: number;
  readonly bytesSent: number;
  /** `null` when the server has sent no receiver report yet. */
  readonly packetsLost: number | null;
  readonly jitterMs: number | null;
  readonly rttMs: number | null;
}

/** Reads the outbound audio stream and the server's report on it. */
export function readUplinkSnapshot(report: RTCStatsReport): UplinkSnapshot | null {
  let outbound: Record<string, unknown> | null = null;
  let remote: Record<string, unknown> | null = null;
  for (const stat of report.values() as Iterable<Record<string, unknown>>) {
    if (stat.kind !== "audio") continue;
    if (stat.type === "outbound-rtp") outbound = stat;
    else if (stat.type === "remote-inbound-rtp") remote = stat;
  }
  if (outbound === null) return null;
  // A receiver report that has arrived carries a round-trip measurement; the
  // stat exists, zeroed, before the first one does.
  const received = remote !== null && numberOr(remote.roundTripTimeMeasurements, 0) > 0
    ? remote
    : null;
  return {
    packetsSent: numberOr(outbound.packetsSent, 0),
    bytesSent: numberOr(outbound.bytesSent, 0),
    packetsLost: received === null ? null : numberOr(received.packetsLost, 0),
    jitterMs: received === null ? null : millis(received.jitter),
    rttMs: received === null ? null : millis(received.roundTripTime),
  };
}

/** The note for one WebRTC recording window. */
export function summarizeRtcUplink(
  recordingMs: number,
  start: UplinkSnapshot,
  atRelease: UplinkSnapshot,
  settled: UplinkSnapshot | null,
): { summary: string; payload: Record<string, unknown> } {
  const sent = Math.max(0, atRelease.packetsSent - start.packetsSent);
  const bytes = Math.max(0, atRelease.bytesSent - start.bytesSent);
  const after = settled ?? atRelease;
  const windowSent = Math.max(0, after.packetsSent - start.packetsSent);
  const lost = after.packetsLost !== null && start.packetsLost !== null
    ? Math.max(0, after.packetsLost - start.packetsLost)
    : null;
  const lossPct = lost !== null && windowSent > 0
    ? Math.round((lost / windowSent) * 1000) / 10
    : null;
  const parts = [
    `recording_ms=${Math.round(recordingMs)}`,
    `packets_sent=${sent}`,
    `bytes_sent=${bytes}`,
    lost === null ? "lost=n/a (no receiver report)" : `lost=${lost}`,
    lossPct === null ? null : `loss=${lossPct}%`,
    after.jitterMs === null ? null : `jitter_ms=${after.jitterMs}`,
    after.rttMs === null ? null : `rtt_ms=${after.rttMs}`,
  ].filter((part): part is string => part !== null);
  return {
    summary: parts.join(" "),
    payload: {
      transport: "webrtc",
      recording_ms: Math.round(recordingMs),
      packets_sent: sent,
      bytes_sent: bytes,
      packets_lost: lost,
      loss_pct: lossPct,
      // Loss is read this long after the window closes, over packets sent in the same span.
      loss_window_extra_ms: settled === null ? 0 : K_UPLINK_REPORT_SETTLE_MS,
      loss_window_packets_sent: windowSent,
      jitter_ms: after.jitterMs,
      rtt_ms: after.rttMs,
    },
  };
}

/** The note for one WebSocket recording window: what was handed to the socket. */
export function summarizeSocketUplink(
  recordingMs: number,
  pcmBytesSent: number,
  sampleRate: number,
  bufferedBytes: number,
): { summary: string; payload: Record<string, unknown> } {
  // PCM16 mono: two bytes a sample.
  const audioMs = Math.round((pcmBytesSent / 2 / sampleRate) * 1000);
  return {
    summary:
      `recording_ms=${Math.round(recordingMs)} audio_ms_sent=${audioMs} socket_buffered_bytes=${bufferedBytes}`,
    payload: {
      transport: "websocket",
      recording_ms: Math.round(recordingMs),
      audio_ms_sent: audioMs,
      // Still queued in the browser when the window closed: the network had
      // not taken it yet.
      socket_buffered_bytes: bufferedBytes,
    },
  };
}

function numberOr(value: unknown, fallback: number): number {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

/** A stats value in seconds, as whole milliseconds. */
function millis(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? Math.round(value * 1000) : null;
}
